import {
    useNavigate,
    useParams,
} from 'react-router-dom'

import {
    useState,
    useEffect,
    useRef,
} from 'react'

import backgroundCheckItemQuery from './services/background-check-item'
import updateBackgroundCheckCountryProviderMutation from './services/update-background-check-country-provider'
import updateIcoverCountryItemMutation from './services/update-icover-country-item'

import { Country } from 'country-state-city'
import {
    useQuery,
    useMutation,
} from '@tanstack/react-query'
import Overlay from '../../Components/Overlay'

import IcoverMappingItem from './Components/IcoverMappingItem'

const allCountries = Country.getAllCountries()

const BackgroundCheckModifyCountryMapping = ({
}) => {

    const navigate = useNavigate()
    const {
        key,
        countryIsoCode,
    } = useParams()

    const icoverMappingItemRef = useRef()

    const {
        data: check,
        isFetching: checkIsFetching,
    } = useQuery({
        queryKey: ['background-check-item', key],
        queryFn: () => backgroundCheckItemQuery(key),
        enabled: !!key,
        refetchOnWindowFocus: false,
    })

    const {
        isPending: updateBackgroundCheckCountryProviderIsPending,
        mutateAsync: updateBackgroundCheckCountryProviderAsync,
    } = useMutation({
        mutationFn: updateBackgroundCheckCountryProviderMutation,
    })

    const {
        isPending: updateIcoverCountryItemIsPending,
        mutateAsync: updateIcoverCountryItemAsync,
    } = useMutation({
        mutationFn: updateIcoverCountryItemMutation,
    })

    useEffect(() => {

        if (!check || !check.key) {
            return
        }

        const currentCountryProvider = check.countryProviders?.[countryIsoCode]

        if (!currentCountryProvider) {
            setIsCountryEditable(true)
            return
        }

        setFormData({
            fulfillment_method: currentCountryProvider.provider === "Internal" ? "Internal" : "External",
            provider: currentCountryProvider.provider,
        })

        const countrySelected = Country.getCountryByCode(countryIsoCode)

        if (!countrySelected) {
            setIsCountryEditable(true)
            return
        }

        setSelectedCountry(countrySelected)


    }, [check])

    const [selectedCountry, setSelectedCountry] = useState()
    const [isCountryEditable, setIsCountryEditable] = useState(false)

    const [formData, setFormData] = useState({
        fulfillment_method: "",
        provider: "",
    })

    const [error, setError] = useState("")

    const vendorOptions = check?.providerList || []

    const handleCountryChange = (countryIsoCode) => {
        const countrySelected = Country.getCountryByCode(countryIsoCode)
        setSelectedCountry(countrySelected)
    }

    const submitUpdateCountryMapping = async () => {

        if (!selectedCountry) {
            setError('Country need to be selected')
            return
        }

        if (formData.fulfillment_method === 'External') {
            if (!formData.provider) {
                setError('Vendor name is required')
                return
            }
        }

        let icoverMappingItemData

        if (formData.provider === "iCover") {
            const icoverMappingItemResult = icoverMappingItemRef.current?.validateAndGetData?.()
            if (!icoverMappingItemResult) {
                setError("Error in iCover Mapping form data")
                return
            }
            const icoverMappingItemIsValid = icoverMappingItemResult.isValid
            if (!icoverMappingItemIsValid) {
                setError("iCover Mapping form is not valid")
                return
            }

            icoverMappingItemData = icoverMappingItemResult.value

        }

        const payload = {
            key: check?.key,
            provider: formData.provider,
            countryIsoCode: selectedCountry?.isoCode,
        }

        await updateBackgroundCheckCountryProviderAsync(payload)

        if (icoverMappingItemData) {
            await updateIcoverCountryItemAsync({
                key: check?.key,
                ...icoverMappingItemData,
            })
        }

        navigate(`/background-check-item/${check.key}/country-settings`)

    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <div className="max-w-5xl mx-auto px-4 py-8 mt-10 bg-white rounded shadow">

                {/* Header */}
                <div className="flex items-start flex-col mb-6">
                    <button
                        type="button"
                        className="text-blue-600 hover:underline text-sm font-medium mb-1"
                        onClick={() => navigate(`/background-check-item/${key}/country-settings`)}
                    >
                        ← Back
                    </button>

                    <h2 className="text-lg font-bold text-gray-800">
                        Background Check Country Mapping - {Country.getCountryByCode(countryIsoCode)?.name || "New"}
                    </h2>

                    {check?.title && (
                        <p className="text-sm text-gray-600 mt-1">
                            {check.title}
                        </p>
                    )}

                    <div>
                        <label className="text-sm text-gray-500 block mb-1">
                            Country
                        </label>
                        <select
                            value={selectedCountry?.isoCode}
                            onChange={(e) => handleCountryChange(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!isCountryEditable}
                        >
                            <option value="">Select a country</option>
                            {allCountries.map((country) => (
                                <option key={country.isoCode} value={country.isoCode}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <p className="font-medium">Fulfillment Method <span className="text-red-500">*</span></p>
                        <div className="mt-2 space-y-2">
                            <label className="flex items-start gap-2">
                                <input
                                    type="radio"
                                    name="fulfillment_method"
                                    value="Internal"
                                    checked={formData.fulfillment_method === 'Internal'}
                                    onChange={() => setFormData(prev => ({
                                        ...prev,
                                        fulfillment_method: 'Internal',
                                        provider: 'Internal',
                                    }))}
                                />
                                <div>
                                    <div className="font-medium">Internal Research</div>
                                    <p className="text-sm text-gray-600">We will do the check ourselves.</p>
                                </div>
                            </label>
                            <label className="flex items-start gap-2">
                                <input
                                    type="radio"
                                    name="fulfillment_method"
                                    value="External"
                                    checked={formData.fulfillment_method === 'External'}
                                    onChange={() => setFormData(prev => ({
                                        ...prev,
                                        fulfillment_method: 'External',
                                        provider: '',
                                    }))}
                                />
                                <div>
                                    <div className="font-medium">External Vendor</div>
                                    <p className="text-sm text-gray-600">Choose a vendor to do this check.</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {formData.fulfillment_method === 'External' && (
                        <div>
                            <label className="block font-medium">Vendor <span className="text-red-500">*</span></label>
                            <select
                                className={`mt-1 w-full border rounded px-3 py-2 ${false ? 'border-red-500' : 'border-gray-300'}`}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    provider: e.target.value,
                                }))}
                                value={formData?.provider}
                            >
                                <option value="">Select Vendor</option>
                                {vendorOptions.map((v) => (
                                    <option key={v} value={v}>{v}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {
                        formData.provider === "iCover" && (
                            <IcoverMappingItem
                                ref={icoverMappingItemRef}
                                selectedCountry={selectedCountry}
                                product={check?.countryProviders?.[selectedCountry.isoCode]}
                            />
                        )
                    }

                    <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end">
                        <button
                            type="button"
                            className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 transition shadow font-medium text-sm"
                            onClick={submitUpdateCountryMapping}
                        >
                            Update Country Mapping
                        </button>
                    </div>

                    {error && (
                        <p className="text-red-600 text-sm mt-2">
                            {error}
                        </p>
                    )}

                </div>

            </div>

            {
                (
                    checkIsFetching ||
                    updateBackgroundCheckCountryProviderIsPending ||
                    updateIcoverCountryItemIsPending
                ) && <Overlay />}
        </div>
    )
}

export default BackgroundCheckModifyCountryMapping
